import {
  isRejectedWithValue,
  isPending,
  isFulfilled,
  Middleware,
  MiddlewareAPI
} from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { toast } from 'sonner';
import * as Sentry from '@sentry/react';

interface RtkQueryError {
  status: number;
  data?: {
    message?: string;
    error?: string;
  };
  message?: string;
}

export const errorLogger: Middleware =
  (api: MiddlewareAPI) => (next) => (action) => {
    // Handle pending actions (optional - for loading states)
    if (isPending(action)) {
      // You can add loading logic here if needed
      // console.log('Pending:', action.type);
    } // ✅ ADD THIS CLOSING BRACE

    // Handle fulfilled actions (optional - for success messages)
    if (isFulfilled(action)) {
      // You can add success logic here if needed
      // console.log('Fulfilled:', action.type);
    } // ✅ ADD THIS CLOSING BRACE

    // Handle rejected actions
    if (isRejectedWithValue(action)) {
      const typedAction = action as PayloadAction<RtkQueryError>;
      let errorMessage = 'An error occurred';
      let statusCode: number | undefined;

      // Extract error information
      if (typedAction.payload) {
        statusCode = typedAction.payload.status;

        // Priority order for error message extraction
        if (typedAction.payload.data?.message) {
          errorMessage = typedAction.payload.data.message;
        } else if (typedAction.payload.data?.error) {
          errorMessage = typedAction.payload.data.error;
        } else if (typedAction.payload.message) {
          errorMessage = typedAction.payload.message;
        } else if (typeof typedAction.payload === 'string') {
          errorMessage = typedAction.payload;
        }
      } else if (action.error?.message) {
        errorMessage = action.error.message;
      }

      // Handle specific HTTP status codes
      switch (statusCode) {
        case 400:
          toast.error('Bad Request: ' + errorMessage);
          break;
        case 401:
          toast.error('Unauthorized: Please login again');
          // Optional: Redirect to login
          // window.location.href = '/signin';
          break;
        case 403:
          toast.error("Forbidden: You don't have permission");
          break;
        case 404:
          toast.error('Not Found: ' + errorMessage);
          break;
        case 500:
          toast.error('Server Error: Please try again later');
          break;
        default:
          toast.error(errorMessage);
      }

      // Development logging
      if (import.meta.env.DEV) {
        console.group('🔴 Redux Error');
        console.log('Action:', action.type);
        console.log('Status:', statusCode);
        console.log('Message:', errorMessage);
        console.log('Payload:', typedAction.payload);
        console.log('Error:', action.error);
        console.groupEnd();
      }

      // Sentry reporting in production
      if (import.meta.env.PROD) {
        Sentry.captureException(new Error(errorMessage), {
          level: statusCode && statusCode >= 500 ? 'error' : 'warning',
          extra: {
            actionType: action.type,
            statusCode,
            payload: typedAction.payload,
            error: action.error,
          },
          tags: {
            errorType: 'redux-query-error',
            httpStatus: statusCode?.toString() || 'unknown',
          },
        });
      }
    }

    // ✅ ALWAYS RETURN next(action)
    return next(action);
  };
